import { ReactElement, useRef } from 'react'
import { useAtom, useAtomValue } from 'jotai'
import { useNavigate } from 'react-router-dom'
import { modalStatusAtom, selectedAccountAtom } from 'store/atoms'
import { balanceTransferOffersModalContainerId } from 'const/modalContainerId'
import {
  ModalHeader,
  SlideInModalPortal
} from 'syf-component-library/ui/patterns/SlideInModalPortal'
import {
  AuthenticationRedirectProps,
  handleMFEAuthenticationRedirect
} from 'helpers/authenticationRedirect'
import routes from 'const/routes'
import getMFEConfig from 'helpers/getMFEConfig'
import BalanceTransfer from '@account-services-mfe/balance-transfer'
import i18n from 'strings/i18n'
import triggerAnalyticsEvent from 'helpers/triggerAnalyticsEvent'
import { getBrandRefreshPhase } from 'helpers/getBrandRefreshPhase'
import useContainerElement from 'hooks/useContainerElement'

const BalanceTransferOffers = (): ReactElement => {
  const navigate = useNavigate()
  const [{ modalId, modalTriggerId }, updateModalStatus] =
    useAtom(modalStatusAtom)
  const headerRef = useRef(null)
  const selectedAccount = useAtomValue(selectedAccountAtom)

  const mfeConfig = getMFEConfig('mfe')
  const containerId = balanceTransferOffersModalContainerId(selectedAccount)
  const isParentElementReady = useContainerElement(containerId)

  const handleCloseModal = () => {
    updateModalStatus('')
  }

  const handleEscapeAndModalHeader = () => {
    triggerAnalyticsEvent({
      pageFunction: 'account services',
      pageKind: 'account services'
    })
    handleCloseModal()
  }

  const handleAuthFlow = () => {
    const props: AuthenticationRedirectProps = {
      navigateTo: routes.ACCOUNT_SERVICES,
      modal: 'BalanceTransferOffers',
      scope: 'read write write:sensitive'
    }
    handleMFEAuthenticationRedirect(props)
  }

  const handleViewPromotions = () => {
    navigate(routes.PROMOTIONS)
  }

  return (
    modalId === 'BalanceTransferOffers' &&
    isParentElementReady && (
      <SlideInModalPortal
        containerId={containerId}
        escapeModalHandler={handleEscapeAndModalHeader}
        id={`bt-slide-in-modal-${selectedAccount}`}
        isOpen={modalId === 'BalanceTransferOffers'}
        modalTriggerId={modalTriggerId}
        header={
          <ModalHeader
            closeModal={handleEscapeAndModalHeader}
            content={i18n({ button: 'balanceTransferOffers' }) as string}
            data-test="balance-transfer-offers-modal-header"
            dataReason="x button"
            dataType="balance transfer"
            headerRef={headerRef}
          />
        }
      >
        <BalanceTransfer
          config={mfeConfig}
          selectedAccountId={selectedAccount}
          handleClose={handleCloseModal}
          authenticationRedirect={handleAuthFlow}
          handleViewPromotions={handleViewPromotions}
          features={{
            brandRefreshPhase: getBrandRefreshPhase()
          }}
        />
      </SlideInModalPortal>
    )
  )
}

export default BalanceTransferOffers
